import { Component, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { defaultTableDefOptions } from '../../../helpers';
import { LocationGroupService } from '../../../service/location-group.service';
import { Branch, LocationBaseResponse, LocationResponse, LocationTiny } from '../../../types';
import { LocationGroup } from '../../../types/location-group-response';
import { CellLocationGroupListActionsComponent } from './cell-locationgroup-list-actions/cell-locationgroup-list-actions.component';
import { CellLocationGroupListHeaderActionsComponent } from './cell-locationgroup-list-header-actions/cell-locationgroup-list-header-actions.component';

@Component({
  selector: 'app-bl-management-locationgroup',
  templateUrl: './bl-management-locationgroup.component.html',
  styleUrls: ['./bl-management-locationgroup.component.scss'],
  standalone: false,
  providers: [],
})
export class BlManagementLocationGroupComponent implements OnInit {
  private locationGroupService = inject(LocationGroupService);

  public locationGroup = input.required<LocationGroup>();
  public selectLocationGroup = output<LocationGroup>();

  public grid = viewChild<AgGridAngular>('locationListGrid');

  public isLoading = signal<boolean>(false);
  public locationGroupLocations = signal<LocationBaseResponse[]>([]);

  public addLocationToGroupModal = viewChild<PanModalComponent>('addLocationToGroup');
  public currentBranch = signal<Branch | undefined>(undefined);
  public currentLocation = signal<LocationTiny | undefined>(undefined);

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 50,
  };

  public readonly locationGroupColumns: ColDef<LocationBaseResponse>[] = [
    {
      field: 'branch_name',
      headerName: 'Branch Name',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'name',
      headerName: 'Location Name',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      headerComponent: CellLocationGroupListHeaderActionsComponent,
      headerClass: ['w-full'],
      headerComponentParams: {
        addCallback: () => {
          this.addLocationToGroupModal().show();
        },
      },
      cellRenderer: CellLocationGroupListActionsComponent,
      cellRendererParams: {
        removeCallback: (data: LocationTiny) => {
          this.locationGroupRemoveLocation(data.id);
        },
      },
    },
  ];

  public readonly locationFilterFn = (location: LocationResponse) => !location.location_group_id;

  ngOnInit() {
    this.loadLocationGroupData();
  }

  loadLocationGroupData() {
    this.isLoading.set(true);

    this.locationGroupService
      .getLocationGroupById({ location_group_id: this.locationGroup().id, include_locations: true })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((data) => {
        this.locationGroupLocations.set(data.locations);
      });
  }

  locationGroupAddLocation() {
    const loc = this.currentLocation();
    if (!loc) return;

    this.locationGroupService.addLocationToGroup(this.locationGroup().id, loc.id).subscribe(() => {
      this.addLocationToGroupModal().hide();
      this.currentBranch.set(undefined);
      this.currentLocation.set(undefined);

      this.loadLocationGroupData();
    });
  }

  locationGroupRemoveLocation(locationId: number) {
    this.locationGroupService.removeLocationFromGroup(this.locationGroup().id, locationId).subscribe(() => {
      this.loadLocationGroupData();
    });
  }
}
